import React from "react";

import { formatDate } from "~/utils/bloodGlucoseFormatters";
import { HealthBreakdown } from "../Shared";

interface BloodGlucoseData {
  date: string;
  low: number;
  high: number;
  avg: number;
  hour?: number;
  datetime?: string;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface BloodGlucoseBreakdownProps {
  selectedRange: TimeRange;
  filteredData: BloodGlucoseData[];
  breakdownTitle: string;
  loading: boolean;
  error: string | null;
}

export const BloodGlucoseBreakdown: React.FC<BloodGlucoseBreakdownProps> = ({
  selectedRange,
  filteredData,
  breakdownTitle,
  loading,
  error,
}) => {
  const getKey = (item: BloodGlucoseData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item.datetime ?? item.date)
      : item.date;
  };

  const getTimeDisplay = (item: BloodGlucoseData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item.hour ?? 0) + ":00"
      : formatDate(item.date, selectedRange);
  };

  const getValues = (item: BloodGlucoseData) => [
    {
      label: "Low",
      value: item.low.toFixed(2),
      color: "#3b82f6", // blue-500
      unit: "mg/dL",
    },
    {
      label: "Avg",
      value: item.avg.toFixed(2),
      color: "#22c55e", // green-500
      unit: "mg/dL",
    },
    {
      label: "High",
      value: item.high.toFixed(2),
      color: "#ef4444", // red-500
      unit: "mg/dL",
    },
  ];

  return (
    <HealthBreakdown
      selectedRange={selectedRange}
      filteredData={filteredData}
      breakdownTitle={breakdownTitle}
      loading={loading}
      error={error}
      dataTypeLabel="Blood Glucose Data"
      getKey={getKey}
      getTimeDisplay={getTimeDisplay}
      getValues={getValues}
      noDataMessage="No blood glucose data available"
    />
  );
};